import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Search, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';

const ParallaxHero = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const containerRef = useRef(null);
  const [searchQuery, setSearchQuery] = React.useState('');

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start start', 'end start']
  });

  const y1 = useTransform(scrollYProgress, [0, 1], ['0%', '50%']);
  const y2 = useTransform(scrollYProgress, [0, 1], ['0%', '30%']);
  const y3 = useTransform(scrollYProgress, [0, 1], ['0%', '20%']);
  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);
  const scale = useTransform(scrollYProgress, [0, 0.5], [1, 0.95]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/category/all?search=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  // Floating images data
  const floatingImages = [
    {
      src: 'https://images.unsplash.com/photo-1523049673857-eb18f1d7b578?w=400',
      alt: 'Avocado',
      className: 'absolute top-20 right-[10%] w-24 md:w-32 lg:w-40',
      y: y1,
      delay: 0,
    },
    {
      src: 'https://images.unsplash.com/photo-1590502593747-42a996133562?w=400',
      alt: 'Lemons',
      className: 'absolute top-40 right-[25%] w-20 md:w-28 lg:w-36',
      y: y2,
      delay: 0.1,
    },
    {
      src: 'https://images.unsplash.com/photo-1464965911861-746a04b4bca6?w=400',
      alt: 'Strawberries',
      className: 'absolute bottom-32 right-[5%] w-28 md:w-36 lg:w-44',
      y: y3,
      delay: 0.2,
    },
    {
      src: 'https://images.unsplash.com/photo-1576045057995-568f588f82fb?w=400',
      alt: 'Spinach',
      className: 'absolute top-60 right-[40%] w-16 md:w-24 lg:w-28 hidden md:block',
      y: y1,
      delay: 0.3,
    },
  ];

  return (
    <section
      ref={containerRef}
      className="relative min-h-screen overflow-hidden bg-gradient-to-b from-lime-50/50 to-background"
      data-testid="parallax-hero"
    >
      {/* Background gradient */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-lime-100/40 via-transparent to-transparent" />

      {/* Floating images */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {floatingImages.map((img, index) => (
          <motion.div
            key={index}
            style={{ y: img.y, opacity }}
            className={img.className}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: img.delay, duration: 0.8, ease: 'easeOut' }}
          >
            <motion.img
              src={img.src}
              alt={img.alt}
              className="w-full h-auto rounded-3xl shadow-floating object-cover animate-float"
              style={{ animationDelay: `${img.delay * 2}s` }}
              loading="eager"
            />
          </motion.div>
        ))}
      </div>

      {/* Content */}
      <motion.div
        style={{ opacity, scale }}
        className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-20 md:pt-40 md:pb-32"
      >
        <div className="max-w-2xl">
          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6"
          >
            <span className="w-2 h-2 rounded-full bg-primary animate-pulse" />
            Fresh & Organic
          </motion.div>

          {/* Headline */}
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-slate-900 mb-6"
            data-testid="hero-title"
          >
            {t('hero.title')}{' '}
            <span className="text-gradient-primary">{t('hero.titleHighlight')}</span>
          </motion.h1>

          {/* Subtitle */}
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="text-lg md:text-xl text-slate-600 leading-relaxed mb-8 max-w-xl"
            data-testid="hero-subtitle"
          >
            {t('hero.subtitle')}
          </motion.p>

          {/* Search Bar */}
          <motion.form
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            onSubmit={handleSearch}
            className="flex flex-col sm:flex-row gap-3 mb-8"
          >
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t('hero.search')}
                className="w-full h-14 pl-12 pr-4 rounded-2xl bg-white/80 backdrop-blur-sm border border-slate-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all shadow-card"
                data-testid="hero-search-input"
              />
            </div>
            <Button
              type="submit"
              className="btn-primary h-14 px-8 text-base"
              data-testid="hero-search-btn"
            >
              {t('common.search')}
            </Button>
          </motion.form>

          {/* CTA Button */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
          >
            <Button
              onClick={() => navigate('/category/all')}
              variant="outline"
              size="lg"
              className="group rounded-full border-2 border-slate-200 hover:border-primary hover:bg-primary/5 px-8 h-14 text-base font-medium"
              data-testid="hero-cta-btn"
            >
              {t('hero.cta')}
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Button>
          </motion.div>

          {/* Stats */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            className="flex flex-wrap gap-8 mt-12 pt-8 border-t border-slate-200"
          >
            {[
              { value: '500+', label: 'Products' },
              { value: '50k+', label: 'Customers' },
              { value: '30min', label: 'Delivery' },
            ].map((stat, index) => (
              <div key={index}>
                <p className="text-2xl md:text-3xl font-bold text-slate-900">{stat.value}</p>
                <p className="text-sm text-slate-500">{stat.label}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </motion.div>

      {/* Bottom gradient overlay */}
      <div className="absolute bottom-0 left-0 right-0 h-32 gradient-hero pointer-events-none" />
    </section>
  );
};

export default ParallaxHero;
